import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  EmptyOption,
  OptionButton,
  OptionsList,
  SearchInput,
  SelectArrow,
  SelectMenu,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from "./Select.styles";
import type { SelectOption, SelectProps } from "./Select.types";

const MENU_GAP = 4;
const MENU_MARGIN = 8;
const ESTIMATED_MENU_HEIGHT = 220;

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Выбрать",
  ariaLabel = "Выбрать значение",
  disabled = false,
  searchable = false,
  searchPlaceholder = "Поиск",
  fullWidth = false,
  minWidth,
  renderOptionPrefix,
  renderValuePrefix,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listboxId = `select-${useId().replace(/:/g, "")}`;

  const selectedOption = useMemo(
    () => options.find((option) => Object.is(option.value, value)),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!searchable || normalizedQuery === "") {
      return options;
    }

    return options.filter((option) => option.label.toLowerCase().includes(normalizedQuery));
  }, [options, query, searchable]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || typeof window === "undefined") {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const measuredMenuHeight = menuRef.current?.getBoundingClientRect().height;
    const menuHeight = measuredMenuHeight || ESTIMATED_MENU_HEIGHT;
    const shouldOpenUpward =
      rect.bottom + MENU_GAP + menuHeight > window.innerHeight - MENU_MARGIN &&
      rect.top > menuHeight;
    const top = shouldOpenUpward
      ? Math.max(MENU_MARGIN, rect.top - MENU_GAP - menuHeight)
      : Math.min(window.innerHeight - MENU_MARGIN, rect.bottom + MENU_GAP);
    const width = Math.max(rect.width, 100);
    const left = Math.min(
      Math.max(MENU_MARGIN, rect.left),
      Math.max(MENU_MARGIN, window.innerWidth - width - MENU_MARGIN),
    );

    setMenuPosition({ top, left, width });
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setQuery("");
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) {
      return;
    }

    const selectedIndex = options.findIndex((option) => Object.is(option.value, value));
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setQuery("");
    setIsOpen(true);
  }, [disabled, options, value]);

  const selectOption = useCallback(
    (option: SelectOption) => {
      if (option.disabled) {
        return;
      }
      onChange(option.value);
      closeMenu();
      triggerRef.current?.focus();
    },
    [closeMenu, onChange],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    updatePosition();
    const raf = window.requestAnimationFrame(() => {
      updatePosition();
      if (searchable) {
        searchRef.current?.focus();
      }
    });

    const handleViewportChange = () => updatePosition();
    const handleOutsidePointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        closeMenu();
      }
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    document.addEventListener("mousedown", handleOutsidePointer);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
      document.removeEventListener("mousedown", handleOutsidePointer);
    };
  }, [closeMenu, isOpen, searchable, updatePosition]);

  useEffect(() => {
    if (filteredOptions.length === 0) {
      setActiveIndex(0);
      return;
    }
    setActiveIndex((previous) => Math.min(previous, filteredOptions.length - 1));
  }, [filteredOptions]);

  const moveActive = (direction: 1 | -1) => {
    if (filteredOptions.length === 0) {
      return;
    }

    setActiveIndex((previous) => {
      let next = previous;
      for (let attempts = 0; attempts < filteredOptions.length; attempts += 1) {
        next = (next + direction + filteredOptions.length) % filteredOptions.length;
        if (!filteredOptions[next]?.disabled) {
          return next;
        }
      }
      return previous;
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!isOpen) {
      if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
        event.preventDefault();
        openMenu();
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveActive(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(-1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = filteredOptions[activeIndex];
      if (option) {
        selectOption(option);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeMenu();
      triggerRef.current?.focus();
    }
  };

  const menu = isOpen && typeof document !== "undefined" ? (
    <SelectMenu
      ref={menuRef}
      $top={menuPosition.top}
      $left={menuPosition.left}
      $width={menuPosition.width}
    >
      {searchable && (
        <SearchInput
          ref={searchRef}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
          placeholder={searchPlaceholder}
          aria-label="Поиск по вариантам"
        />
      )}
      <OptionsList id={listboxId} role="listbox" aria-label={ariaLabel}>
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => {
            const selected = Object.is(option.value, value);
            return (
              <li key={`${String(option.value)}-${index}`} role="presentation">
                <OptionButton
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={option.disabled}
                  $active={index === activeIndex}
                  $selected={selected}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => selectOption(option)}
                >
                  {renderOptionPrefix?.(option)}
                  <span>{option.label}</span>
                </OptionButton>
              </li>
            );
          })
        ) : (
          <li role="presentation">
            <EmptyOption>Нет совпадений</EmptyOption>
          </li>
        )}
      </OptionsList>
    </SelectMenu>
  ) : null;

  return (
    <SelectRoot $fullWidth={fullWidth} $minWidth={minWidth}>
      <SelectTrigger
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-haspopup="listbox"
        disabled={disabled}
        $open={isOpen}
        onClick={() => (isOpen ? closeMenu() : openMenu())}
        onKeyDown={handleKeyDown}
      >
        <SelectValue>
          {selectedOption && renderValuePrefix?.(selectedOption)}
          <span>{selectedOption?.label || placeholder}</span>
        </SelectValue>
        <SelectArrow $open={isOpen} aria-hidden="true" />
      </SelectTrigger>
      {menu && createPortal(menu, document.body)}
    </SelectRoot>
  );
};

export default Select;
